import { useCallback, useRef, useState } from 'react'

/**
 * Pinch-to-zoom and pan, implemented by hand.
 *
 * The viewport is set to user-scalable=no so the app does not feel like a web
 * page — no double-tap zoom lag, no accidental zoom while scrolling a list.
 * That also disables native pinch, and reading a faded thermal receipt is
 * exactly when you need it. So the gesture is reimplemented here, scoped to
 * the document viewer only.
 *
 * Deliberately simple: no momentum, no rotation. This is for reading a number
 * off an invoice, not for photo editing.
 */

const MAX_SCALE = 6
const MIN_SCALE = 1
const DOUBLE_TAP_SCALE = 2.5
const DOUBLE_TAP_MS = 300

interface Transform {
  scale: number
  x: number
  y: number
}

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 }

export function ZoomableImage({ src, alt }: { src: string; alt: string }) {
  const [t, setT] = useState<Transform>(IDENTITY)
  const containerRef = useRef<HTMLDivElement>(null)

  // Gesture bookkeeping. Refs rather than state: these change every frame and
  // must not trigger re-renders.
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gestureStart = useRef<{ distance: number; scale: number; cx: number; cy: number } | null>(null)
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null)
  const lastTap = useRef(0)

  const clamp = useCallback((next: Transform): Transform => {
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale))
    const el = containerRef.current
    if (!el || scale === 1) return { scale, x: 0, y: 0 }

    // Do not let the image be dragged entirely off screen.
    const limitX = ((scale - 1) * el.clientWidth) / 2
    const limitY = ((scale - 1) * el.clientHeight) / 2
    return {
      scale,
      x: Math.max(-limitX, Math.min(limitX, next.x)),
      y: Math.max(-limitY, Math.min(limitY, next.y)),
    }
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      gestureStart.current = {
        distance: Math.hypot(a!.x - b!.x, a!.y - b!.y),
        scale: t.scale,
        cx: (a!.x + b!.x) / 2,
        cy: (a!.y + b!.y) / 2,
      }
      panStart.current = null
    } else if (pointers.current.size === 1) {
      panStart.current = { x: e.clientX, y: e.clientY, tx: t.x, ty: t.y }

      const now = Date.now()
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        setT((prev) => clamp(prev.scale > 1 ? IDENTITY : { ...IDENTITY, scale: DOUBLE_TAP_SCALE }))
        lastTap.current = 0
      } else {
        lastTap.current = now
      }
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointers.current.size === 2 && gestureStart.current) {
      const [a, b] = [...pointers.current.values()]
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y)
      const ratio = distance / gestureStart.current.distance
      setT((prev) => clamp({ ...prev, scale: gestureStart.current!.scale * ratio }))
      return
    }

    // Single finger pans, but only once zoomed in — otherwise a swipe should
    // page through the document rather than drag a fitted image around.
    if (pointers.current.size === 1 && panStart.current && t.scale > 1) {
      e.preventDefault()
      setT((prev) =>
        clamp({
          ...prev,
          x: panStart.current!.tx + (e.clientX - panStart.current!.x),
          y: panStart.current!.ty + (e.clientY - panStart.current!.y),
        }),
      )
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size < 2) gestureStart.current = null
    if (pointers.current.size === 0) panStart.current = null
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-black grid place-items-center"
      style={{ touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="max-w-full max-h-full object-contain select-none"
        style={{
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          // No transition while pinching, or it fights the gesture.
          transition: gestureStart.current ? 'none' : 'transform 150ms ease-out',
        }}
      />

      {t.scale > 1 && (
        <button
          onClick={() => setT(IDENTITY)}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full
                     bg-white/90 text-ink text-sm font-medium shadow"
        >
          Passend maken
        </button>
      )}
    </div>
  )
}
